
export const SUPER_ADMIN_EMAILS: string[] = ["ad@lg.app", "king@lg.app", "ken@lg.app"];
export const LEGAL_EMAILS: string[] = ["kc@lg.app", "lg@lg.app"];
export const SUPERVISOR_LISA_EMAILS: string[] = ["lisa@lg.app"];
export const SUPERVISOR_IRENE_EMAILS: string[] = ["irene@lg.app"];
export const SEAL_EMAILS: string[] = ["seal@lg.app"];

// The Specific Department Accounts
export const DEPT_MINIMAL_EMAILS: string[] = [
  "gm@lg.app",  // 總經理部
  "fa@lg.app",  // 財務部
  "ap@lg.app",  // 行政人事部
  "it@lg.app",  // 資訊部 (Downgraded from Super)
  "lg@lg.app",  // 法務部
  "sl1@lg.app", // 業務1部
  "sl2@lg.app", // 業務2部
  "li@lg.app",  // 物流部
  "pt@lg.app",  // 採購部
  "qc@lg.app",  // 品保部
  "md@lg.app",  // 行銷設計部
];

// Accounts to be Temporarily Suspended
export const SUSPENDED_EMAILS: string[] = [];

// Read-Only Super Users (Can view everything, cannot write)
export const READ_ONLY_SUPER_EMAILS: string[] = ["js@lg.app"];

// Super Users who can view ALL data regardless of department
// Note: IT is removed from here. JS is added via spread.
export const SUPER_USER_EMAILS: string[] = [
  "ad@lg.app",
  "kc@lg.app",
  "lisa@lg.app",
  "irene@lg.app",
  "king@lg.app",
  "ken@lg.app",
  ...READ_ONLY_SUPER_EMAILS
];

// ★ SENSITIVE DATA ACCESS
export const SENSITIVE_DATA_VIEWERS: string[] = [
  "kc@lg.app", 
  "ad@lg.app",
  ...READ_ONLY_SUPER_EMAILS
];

export const WORKLOG_VIEWER_EMAILS = SENSITIVE_DATA_VIEWERS;
export const AUDIT_VIEWER_EMAILS = SENSITIVE_DATA_VIEWERS;

// Debug View Allowed
export const ALLOWED_DEBUG_VIEWERS: string[] = [
  "ad@lg.app", "kc@lg.app", "king@lg.app", "ken@lg.app", 
  ...READ_ONLY_SUPER_EMAILS
];

export const DEBUG_VIEW_DEPT_CODES: string[] = [
  "GM", "FA", "AP", "IT", "LG", "SL1", "SL2", "LI", "PT", "QC", "MD"
];

// Permission Helpers
export const PROJECT_DELETION_ALLOWED_EMAILS = ["kc@lg.app", "ad@lg.app"];

export function canDeleteComplianceProject(email?: string | null): boolean {
  if (!email) return false;
  return PROJECT_DELETION_ALLOWED_EMAILS.includes(email.toLowerCase());
}

export type UserRole = 'superAdmin' | 'legal' | 'executive' | 'sealUser' | 'staff' | 'viewer' | 'dept-minimal' | 'superViewer' | 'legacyDataEntry';

export function getRoleByEmail(email?: string | null): UserRole {
  if (!email) return 'viewer';
  const lower = email.toLowerCase();
  
  if (SUPER_ADMIN_EMAILS.some(e => lower === e)) return 'superAdmin';
  if (READ_ONLY_SUPER_EMAILS.some(e => lower === e)) return 'superViewer';
  
  if (DEPT_MINIMAL_EMAILS.some(e => lower === e)) return 'dept-minimal';
  
  if (SUPERVISOR_LISA_EMAILS.some(e => lower === e)) return 'executive';
  if (SUPERVISOR_IRENE_EMAILS.some(e => lower === e)) return 'executive';
  
  if (LEGAL_EMAILS.some(e => lower === e)) return 'legal'; 
  
  if (lower === 'ptkeyin@lg.app') return 'legacyDataEntry';

  return 'staff';
}

export function isUserSuspended(email?: string | null): boolean {
  if (!email) return false;
  return SUSPENDED_EMAILS.includes(email.toLowerCase());
}

export function getDeptCodeByEmail(email?: string | null): string {
  if (!email) return "";
  const prefix = email.split("@")[0].toLowerCase();
  const map: Record<string, string> = {
    "gm": "GM", "fa": "FA", "ap": "AP", "it": "IT", "lg": "LG",
    "sl1": "SL1", "sl2": "SL2", "li": "LI", "pt": "PT", "qc": "QC", "md": "MD",
    "king": "SYS", "ken": "SYS", "kc": "LG", "ad": "LG", "kevin": "LG",
    "js": "SYS"
  };
  return map[prefix] || "OTHER";
}

export function isSuperUser(email?: string | null): boolean {
  if (!email) return false;
  return SUPER_USER_EMAILS.includes(email.toLowerCase());
}

export function isReadOnlyUser(email?: string | null): boolean {
  if (!email) return false;
  return READ_ONLY_SUPER_EMAILS.includes(email.toLowerCase());
}

export function canInitiateLegalRequest(role: UserRole): boolean {
  if (role === 'superViewer') return false; 
  return (
    role === 'superAdmin' ||
    role === 'legal' ||
    role === 'executive' ||
    role === 'sealUser' ||
    role === 'dept-minimal'
  );
}

export function isGovAdmin(email?: string | null): boolean {
  if (!email) return false;
  if (isReadOnlyUser(email)) return false;
  return SUPER_USER_EMAILS.includes(email.toLowerCase());
}

export function canManageAllRequests(email?: string | null): boolean {
  // Read-only super users cannot manage/edit
  if (isReadOnlyUser(email)) return false;
  return isGovAdmin(email);
}

export function canViewAllData(email?: string | null): boolean {
  return isSuperUser(email);
}

export function canViewWorklog(email?: string): boolean {
  if (!email) return false;
  return SENSITIVE_DATA_VIEWERS.includes(email.toLowerCase());
}

export function canViewAuditLog(email?: string): boolean {
  if (!email) return false;
  return SENSITIVE_DATA_VIEWERS.includes(email.toLowerCase());
}

export function canUseDebugView(email?: string | null): boolean {
  if (!email) return false;
  return ALLOWED_DEBUG_VIEWERS.includes(email.toLowerCase());
}

export type ModulePermissionKey = "TASKS_AND_LOG" | "CONTRACTS" | "CASES" | "IP" | "BRAND_ASSETS" | "GOV_FILES" | "TEMPLATES" | "GOV_CORRESPONDENCE" | "LAWYER" | "SIGN_SYSTEM" | "AUDIT" | "GOV_DOCS" | "GOV_PROJECTS" | "REG_COMPLIANCE" | "REG_IMPACT_TRACKING" | "OTHERS" | "LEGACY_ENTRY";

export function canEditModule(role: UserRole, module: ModulePermissionKey): boolean {
  // Global Read-Only Check for SuperViewers
  if (role === 'superViewer') return false;

  if (role === 'legacyDataEntry') return module === 'LEGACY_ENTRY';

  if (role === 'superAdmin' || role === 'legal') return true;
  
  if (role === 'executive') {
    if (module === 'SIGN_SYSTEM') return true; 
    return false; 
  }

  if (role === 'dept-minimal') {
    if (module === 'SIGN_SYSTEM' || module === 'REG_IMPACT_TRACKING') return true; 
    return false;
  }

  return false; 
}

export function canSeeModule(role: UserRole, module: ModulePermissionKey): boolean {
  if (role === 'legacyDataEntry') return module === 'LEGACY_ENTRY';
  if (module === 'LEGACY_ENTRY') return role === 'superAdmin' || role === 'legal';

  if (role === 'superViewer') return true;

  if (role === 'superAdmin' || role === 'legal') return true;
  
  if (role === 'executive') {
    if (module === 'TASKS_AND_LOG' || module === 'AUDIT') return false;
    return true; 
  }

  if (role === 'dept-minimal') {
    const allowed = ['SIGN_SYSTEM', 'GOV_FILES', 'REG_COMPLIANCE', 'REG_IMPACT_TRACKING', 'TEMPLATES', 'CONTRACTS'];
    return allowed.includes(module);
  }

  return true;
}

// Helper identities
export function isSuperAdmin(email?: string | null) { return getRoleByEmail(email) === 'superAdmin'; }
export function isLegal(email?: string | null) { return LEGAL_EMAILS.includes(email?.toLowerCase() || '') || isSuperAdmin(email); }
export function isManager(email?: string | null) { const r = getRoleByEmail(email); return r === 'executive' || r === 'superAdmin'; }
export function isLisa(email?: string | null) { return SUPERVISOR_LISA_EMAILS.includes(email?.toLowerCase() || '') || isSuperAdmin(email); }
export function isIrene(email?: string | null) { return SUPERVISOR_IRENE_EMAILS.includes(email?.toLowerCase() || '') || isSuperAdmin(email); }
export function isDeptMinimal(email?: string | null) { return getRoleByEmail(email) === 'dept-minimal'; }
